import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/salon/Navbar';
import BookingModal from '@/components/salon/BookingModal';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { User, Calendar, CreditCard, Heart, Package, Edit2, Save, CalendarPlus, Star, XCircle, Loader } from 'lucide-react';

const API = '/api';

const UserProfilePage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'profile');
  const [loading, setLoading] = useState(false);
  const [hairTypeLoading, setHairTypeLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  
  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    hair_type: '',
    favorite_stylist_id: ''
  });
  
  // Bookings
  const [bookings, setBookings] = useState([]);
  const [cancellingBookingId, setCancellingBookingId] = useState(null);
  
  // Orders
  const [orders, setOrders] = useState([]);
  
  // Services for booking modal
  const [services, setServices] = useState([]);
  
  // Stylists for favorite selection
  const [stylists, setStylists] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadProfileData();
    fetchServices();
    fetchStylists();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'appointments') {
      fetchBookings();
    } else if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'wallet') {
      fetchPaymentMethods();
    }
  }, [activeTab]);

  useEffect(() => {
    const cardStatus = new URLSearchParams(window.location.search).get('card');
    if (cardStatus === 'added') {
      toast.success('Karte wurde sicher hinzugefügt');
    } else if (cardStatus === 'cancelled') {
      toast.info('Karte wurde nicht hinzugefügt');
    }
    if (cardStatus) {
      window.history.replaceState({}, '', '/profile?tab=wallet');
    }
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services', error);
    }
  };

  const fetchStylists = async () => {
    try {
      const response = await axios.get(`${API}/stylists`);
      setStylists(response.data);
    } catch (error) {
      console.error('Failed to fetch stylists', error);
    }
  };

  const loadProfileData = () => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        hair_type: user.hair_type || '',
        favorite_stylist_id: user.favorite_stylist_id || ''
      });
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings/my`, { headers: getAuthHeaders() });
      setBookings(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Termine');
    }
  };

  const handleCancelBooking = async (booking) => {
    const confirmed = window.confirm(
      `Möchten Sie den Termin am ${booking.date} um ${booking.time} Uhr wirklich stornieren?`
    );
    if (!confirmed) return;

    setCancellingBookingId(booking.id);
    try {
      await axios.patch(`${API}/bookings/${booking.id}/cancel`, null, {
        headers: getAuthHeaders(),
      });
      toast.success('Termin wurde storniert. Eine Bestätigung wird per E-Mail gesendet.');
      await fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Termin konnte nicht storniert werden');
    } finally {
      setCancellingBookingId(null);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      confirmed: 'Bestätigt',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert',
      pending_payment: 'Zahlung ausstehend',
      pending: 'Offen',
      processing: 'In Bearbeitung',
      shipped: 'Versendet',
      delivered: 'Geliefert'
    };
    return labels[status] || status;
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/my`, { headers: getAuthHeaders() });
      setOrders(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellungen');
    }
  };

  const fetchPaymentMethods = async () => {
    setWalletLoading(true);
    try {
      const response = await axios.get(`${API}/billing/payment-methods`, { headers: getAuthHeaders() });
      setPaymentMethods(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Zahlungsmethoden konnten nicht geladen werden');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleAddCard = async () => {
    setWalletLoading(true);
    try {
      const response = await axios.post(
        `${API}/billing/payment-methods/setup`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Karte konnte nicht hinzugefügt werden');
      setWalletLoading(false);
    }
  };

  const handleHairTypeChange = async (hairType) => {
    const previousHairType = profileData.hair_type;
    setProfileData((current) => ({ ...current, hair_type: hairType }));
    setHairTypeLoading(true);
    try {
      const params = new URLSearchParams({ hair_type: hairType });
      await axios.put(
        `${API}/profile/update?${params.toString()}`,
        null,
        { headers: getAuthHeaders() }
      );
      updateUser({ ...user, hair_type: hairType });
      toast.success('Haartyp gespeichert');
    } catch (error) {
      setProfileData((current) => ({ ...current, hair_type: previousHairType }));
      toast.error('Haartyp konnte nicht gespeichert werden');
    } finally {
      setHairTypeLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (profileData.name) params.append('name', profileData.name);
      if (profileData.phone !== undefined) params.append('phone', profileData.phone);
      if (profileData.hair_type !== undefined) params.append('hair_type', profileData.hair_type);
      if (profileData.favorite_stylist_id !== undefined) params.append('favorite_stylist_id', profileData.favorite_stylist_id);
      
      await axios.put(
        `${API}/profile/update?${params.toString()}`,
        null,
        { headers: getAuthHeaders() }
      );
      updateUser({ ...user, ...profileData });
      toast.success('Profil aktualisiert');
      setIsEditing(false);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      pending_payment: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Banner with Book Button */}
        <div className="mb-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight mb-2">
                Willkommen, {user?.name || 'Kunde'}! 👋
              </h1>
              <p className="text-blue-100">Verwalten Sie Ihre Termine und buchen Sie Ihren nächsten Besuch</p>
            </div>
            <Button
              onClick={() => setIsBookingModalOpen(true)}
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 py-3 text-lg"
            >
              <CalendarPlus className="w-5 h-5 mr-2" />
              Jetzt Buchen
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Profil</span>
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Termine</span>
            </TabsTrigger>
            <TabsTrigger value="wallet" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Wallet</span>
            </TabsTrigger>
            <TabsTrigger value="favorites" className="flex items-center gap-2">
              <Heart className="w-4 h-4" />
              <span className="hidden sm:inline">Favoriten</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Bestellungen</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-slate-900">Persönliche Daten</h2>
                <Button
                  onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isEditing ? (
                    <><Save className="w-4 h-4 mr-2" /> Speichern</>
                  ) : (
                    <><Edit2 className="w-4 h-4 mr-2" /> Bearbeiten</>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    disabled={!isEditing}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>E-Mail</Label>
                  <Input
                    value={profileData.email}
                    disabled
                    className="mt-2 bg-slate-100"
                  />
                </div>

                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    disabled={!isEditing}
                    placeholder="+49 123 456789"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Haartyp (für personalisierte Pflegeberatung)</Label>
                  <select
                    value={profileData.hair_type}
                    onChange={(e) => handleHairTypeChange(e.target.value)}
                    disabled={hairTypeLoading}
                    className="w-full mt-2 p-2 border border-slate-200 rounded-lg disabled:opacity-60"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="Normal">Normal</option>
                    <option value="Trocken">Trocken</option>
                    <option value="Fein">Fein</option>
                    <option value="Glatt">Glatt</option>
                    <option value="Fettend">Fettend</option>
                    <option value="Schuppig">Schuppig</option>
                    <option value="Strapaziert">Strapaziert</option>
                  </select>
                  <p className="text-sm text-slate-500 mt-1">
                    {hairTypeLoading ? 'Wird gespeichert …' : 'Wird nach der Auswahl automatisch gespeichert'}
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    Mein Favorit-Stylist
                  </Label>
                  <select
                    value={profileData.favorite_stylist_id}
                    onChange={(e) => setProfileData({...profileData, favorite_stylist_id: e.target.value})}
                    disabled={!isEditing}
                    className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
                  >
                    <option value="">Keinen Favoriten ausgewählt</option>
                    {stylists.map((stylist) => (
                      <option key={stylist.id} value={stylist.id}>
                        {stylist.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-slate-500 mt-1">
                    Wird automatisch bei neuen Buchungen vorausgewählt
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="p-6">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">Meine Termine</h2>
              {bookings.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-600">Keine Termine vorhanden</p>
                  <Button
                    onClick={() => navigate('/')}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    Jetzt buchen
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <Card key={booking.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-slate-900">{booking.service_name}</h3>
                            <Badge className={getStatusColor(booking.status)}>{getStatusLabel(booking.status)}</Badge>
                          </div>
                          {booking.stylist_name && (
                            <p className="text-sm text-slate-600 mb-1">Stylist: {booking.stylist_name}</p>
                          )}
                          <p className="text-sm text-slate-600">
                            📅 {booking.date} um {booking.time} Uhr
                          </p>
                          {booking.notes && (
                            <p className="text-sm text-slate-500 mt-2">Notiz: {booking.notes}</p>
                          )}
                        </div>
                        <div className="text-right space-y-3">
                          <p className="text-xl font-bold text-slate-900">€{booking.service_price}</p>
                          {booking.status === 'confirmed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelBooking(booking)}
                              disabled={cancellingBookingId === booking.id}
                              className="border-red-200 text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              {cancellingBookingId === booking.id ? 'Wird storniert …' : 'Termin stornieren'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Wallet Tab */}
          <TabsContent value="wallet">
            <Card className="p-6">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">Wallet & Zahlungsmethoden</h2>
              <div className="max-w-xl mx-auto py-8">
                <div className="text-center mb-6">
                  <CreditCard className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-600 mb-2">Kreditkarten-Verwaltung</p>
                  <p className="text-sm text-slate-500">Speichern Sie Ihre Karte sicher für Onlinezahlungen</p>
                </div>

                {walletLoading && paymentMethods.length === 0 ? (
                  <div className="flex justify-center py-6">
                    <Loader className="w-6 h-6 text-blue-600 animate-spin" />
                  </div>
                ) : paymentMethods.length > 0 ? (
                  <div className="space-y-3 mb-6">
                    {paymentMethods.map((method) => (
                      <div key={method.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-6 h-6 text-blue-600" />
                          <div>
                            <p className="font-semibold text-slate-900 capitalize">{method.brand} •••• {method.last4}</p>
                            <p className="text-sm text-slate-500">Gültig bis {String(method.exp_month).padStart(2, '0')}/{method.exp_year}</p>
                          </div>
                        </div>
                        <Badge className="bg-green-100 text-green-700">Gespeichert</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center mb-6">Noch keine Karte gespeichert</p>
                )}

                <div className="text-center">
                  <Button
                    onClick={handleAddCard}
                    disabled={walletLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {walletLoading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Karte hinzufügen
                  </Button>
                  <p className="text-xs text-slate-400 mt-4">Sichere Kartenspeicherung über Stripe – Salon Galo sieht keine Kartennummer</p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <Card className="p-6">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">Meine Favoriten</h2>
              <div className="text-center py-12">
                <Heart className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">Noch keine Favoriten gespeichert</p>
                <p className="text-sm text-slate-500 mt-2">Speichern Sie Ihre Lieblings-Services und Stylisten</p>
              </div>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="p-6">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">Meine Bestellungen</h2>
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-600">Keine Bestellungen vorhanden</p>
                  <Button
                    onClick={() => navigate('/shop')}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    Zum Shop
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <Card key={order.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-slate-900">Bestellung #{order.id.slice(0, 8)}</h3>
                            <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">
                            {new Date(order.created_at).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-slate-900">€{order.total.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="border-t pt-3">
                        <p className="text-sm font-medium text-slate-700 mb-2">Produkte:</p>
                        {order.items.map((item, idx) => (
                          <p key={idx} className="text-sm text-slate-600">
                            {item.product_name} x{item.quantity} - €{item.product_price}
                          </p>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        services={services}
      />
    </div>
  );
};

export default UserProfilePage;